import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { URLS } from '../constants/URLS';

@Injectable({ providedIn: 'root' })
export class WhiteLabelService {
  public channel: string | null = null;
  public entryType: string | null = null;
  private _channelId$ = new BehaviorSubject<string | null>(null);
  readonly channelId$ = this._channelId$.asObservable();
  private _isWhiteLabel = false;
  private _channel: string | null = null;   
  private _entry: string | null = null;   
  private _ready = false;
  private _readyResolve!: () => void;
  private _readyPromise: Promise<void> = new Promise(res => (this._readyResolve = res));
  private resolvingIdPromise: Promise<void> | null = null;
  private _invalidWL = false;

  constructor(
    private router: Router,
    private http: HttpClient
  ) {}

  async init(): Promise<void> {
    const cachedId = localStorage.getItem('wl_content_provider_id');
    if (cachedId) {
      this._channelId$.next(cachedId);
    }
    this.detectFromDomain();
    this.detect(window.location.pathname);
    await this.ensureChannelIdIfWhiteLabel();
    this.router.events.subscribe(async e => {
      if (e instanceof NavigationEnd) {
        this.detectFromDomain();
        this.detect(e.urlAfterRedirects);
        await this.ensureChannelIdIfWhiteLabel();
      }
    });
    window.addEventListener('visibilitychange', async () => {
      if (document.visibilityState === 'visible' && this._isWhiteLabel && !this.currentChannelId()) {
        await this.ensureChannelIdIfWhiteLabel();
      }
    });
    this.markReady();
  }

  async mustHaveChannelId(): Promise<void> {
    if (!this._isWhiteLabel) return;
    if (this.currentChannelId()) return;
    if (!this.resolvingIdPromise) {
      this.resolvingIdPromise = (async () => {
        await this.ensureChannelIdIfWhiteLabel();
        if (this.currentChannelId()) return;
        await new Promise(r => setTimeout(r, 300));
        await this.ensureChannelIdIfWhiteLabel();
      })().finally(() => {
        this.resolvingIdPromise = null;
      });
    }
    await this.resolvingIdPromise;
  }

  detect(path: string): boolean {
    const segments = path.split('/').filter(Boolean);
    const channelIndex = segments.findIndex(s => s.startsWith('@'));
    const storedChannel = localStorage.getItem('wl_channel');
    const storedEntry = localStorage.getItem('wl_entry');
    const storedId = localStorage.getItem('wl_content_provider_id');
    if (storedChannel && storedEntry && storedId) {
      const pathSegments = path.split('/').filter(Boolean);
      const isAuthRoute = pathSegments.includes('login') || pathSegments.includes('registration') || pathSegments.includes('wl-invalid');
      if (!path.includes(storedChannel) && !isAuthRoute) {
        this.router.navigate([storedChannel, storedEntry]);
      }
      this._isWhiteLabel = true;
      this._channel = storedChannel;
      this._entry = storedEntry;
      this.channel = storedChannel;
      this.entryType = storedEntry;
      return true;
    }
    if (channelIndex !== -1) {
      const channel = segments[channelIndex];
      const entry = segments[channelIndex + 1] as 'f' | 'v';
      if (entry === 'f' || entry === 'v') {
        localStorage.setItem('wl_channel', channel);
        localStorage.setItem('wl_entry', entry);
        this._isWhiteLabel = true;
        this._channel = channel;
        this._entry = entry;
        this.channel = channel;
        this.entryType = entry;
        return true;
      }
    }
    localStorage.removeItem('wl_channel');
    localStorage.removeItem('wl_entry');
    localStorage.removeItem('wl_content_provider_id');
    this._isWhiteLabel = false;
    return false;
  }
  
  get isInvalidWhiteLabel() { 
    return this._invalidWL; 
  }

  private async ensureChannelIdIfWhiteLabel(): Promise<void> {
    if (!this._isWhiteLabel) return;
    const existing = this._channelId$.value || localStorage.getItem('wl_content_provider_id');
    if (existing) {
      this._channelId$.next(existing);
      return;
    }
    const wlChannel = this._channel || localStorage.getItem('wl_channel');
    if (!wlChannel) return;
    try {
      const url = `${URLS.getChannelId}/${encodeURIComponent(wlChannel)}`;
      const resp = await this.http.get<{data?: {channelId?: string}}>(url).toPromise();
      const id = resp?.data?.channelId;
      if (!id) {
        this._invalidWL = true;
        this._channelId$.next(null);
        localStorage.removeItem('wl_content_provider_id');
        return;
      }
      this._invalidWL = false;
      localStorage.setItem('wl_content_provider_id', id);
      this._channelId$.next(id);
    } catch {
      this._invalidWL = true;
      this._channelId$.next(null);
    }
  }

  private markReady(): void {
    if (!this._ready) {
      this._ready = true;
      this._readyResolve();
    }
  }

  waitUntilReady(): Promise<void> {
    return this._ready ? Promise.resolve() : this._readyPromise;
  }

  isWhiteLabel(): boolean {
    return this._isWhiteLabel;
  }

  currentChannelId(): string | null {
    return this._channelId$.value || localStorage.getItem('wl_content_provider_id');
  }

  getPrefixedRoute(...segments: string[]): string {
    const channel = this.channel || localStorage.getItem('wl_channel');
    const entry = this.entry || localStorage.getItem('wl_entry');
    if (this._isWhiteLabel && channel && entry) {
      if (segments[0] === entry) {
        return `/${channel}/${segments.join('/')}`;
      }
      return `/${channel}/${entry}/${segments.join('/')}`;
    }
    return `/${segments.join('/')}`;
  }

  get showFlix(): boolean {
    return !this._isWhiteLabel || this.entry === 'f';
  }

  get showVids(): boolean {
    return !this._isWhiteLabel || this.entry === 'v';
  }

  get entry(): string | null {
    return this.entryType;
  }

  get isWhiteLabelMode(): boolean {
    return this._isWhiteLabel;
  }

  get shouldShowChannels(): boolean {
    return !this._isWhiteLabel || this.entry === 'f';
  }

  private detectFromDomain(): boolean {
    const host = window.location.hostname.toLowerCase();
    // if (host === 'ui.flixlocal.com' ||  host === 'localhost' || host === 'homecinemaplus.tv') {
    if (host === 'ui.flixlocal.com' ||  host === 'homecinemaplus.tv' || host === 'www.homecinemaplus.tv') {
      document.title = 'HomeCinema+';
      const existingCpId = localStorage.getItem('wl_content_provider_id');
      if (existingCpId) {
        this._channelId$.next(existingCpId);
        return true;
      }
      this._isWhiteLabel = true;
      const cpId = '11840'; 
      localStorage.setItem('wl_content_provider_id', cpId);
      this._channelId$.next(cpId);
      localStorage.setItem('wl_channel', '@HomeCinema');
      localStorage.setItem('wl_entry', 'f');
      this._channel = '@HomeCinema';
      this._entry = 'f';
      this.channel = '@HomeCinema';
      this.entryType = 'f';
      return true;
    }
    return false;
  }
  // private detectFromDomain(): boolean {
  //   const host = window.location.hostname.toLowerCase();
  //   if (host === 'ui.flixlocal.com' ||  host === 'localhost') {
  //     this._isWhiteLabel = true;
  //     const cpId = '11840';
  //     localStorage.setItem('wl_content_provider_id', cpId);
  //     this._channelId$.next(cpId);
  //     localStorage.setItem('wl_channel', '@Gundu99');
  //     localStorage.setItem('wl_entry', 'f');
  //     return true;
  //   }
  //   return false;
  // }
}


  // detect(path: string): boolean {
  //   const segments = path.split('/').filter(Boolean);
  //   const channelIndex = segments.findIndex(segment => segment.startsWith('@'));
  //   const storedChannel = localStorage.getItem('wl_channel');
  //   const storedEntry = localStorage.getItem('wl_entry');
  //   if (channelIndex !== -1) {
  //     const channel = segments[channelIndex];
  //     const entry = segments[channelIndex + 1];
  //     localStorage.setItem('wl_channel', channel);
  //     if (entry === 'f' || entry === 'v') {
  //       localStorage.setItem('wl_entry', entry);
  //       this.entryType = entry as 'f' | 'v';
  //     }
  //     this.channel = channel;
  //     this.isWhiteLabel = true;
  //     return true;
  //   } else if (storedChannel && storedEntry) {
  //     this.router.navigate([ 'ui', storedChannel, storedEntry ]);
  //     this.channel = storedChannel;
  //     this.entryType = storedEntry as 'f' | 'v';
  //     this.isWhiteLabel = true;
  //     return true;
  //   }
  //   localStorage.removeItem('wl_channel');
  //   localStorage.removeItem('wl_entry');
  //   this.channel = null;
  //   this.entryType = null;
  //   this.isWhiteLabel = false;
  //   return false;
  // }  